import { Box, BoxProps, Checkbox, Flex, FormLabel, useToken } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";
import { Marker } from "react-map-gl/maplibre";

import { CancelIcon } from "@nens/ranalib";

import { RanaMap } from "@/lib/rana-map";

import { useRanaFormMapInput } from "./use-rana-form-map-input";

interface RanaFormInputProps extends BoxProps {
  field: string;
  mapId: string;
  maptilerApiKey: string;
  isReadOnly?: boolean;
  label?: string;
}

export function RanaFormMapInput({
  field,
  mapId,
  maptilerApiKey,
  isReadOnly,
  label,
  ...rest
}: RanaFormInputProps) {
  const [keepAnalytic] = useToken("colors", ["brand.keep_analytic"]);
  const intl = useIntl();
  const {
    value,
    error,
    extra,
    clearValue,
    clickMap,
    onMoveEnd,
    filterGeoByMapBbox,
    setFilterGeoByMapBbox,
    bounds,
  } = useRanaFormMapInput(field, mapId);

  return (
    <Box id={mapId} position="relative" h="full" {...rest}>
      {label && <FormLabel>{label}</FormLabel>}
      <Box rounded="base" borderWidth="base" overflow="hidden" h="sm">
        <RanaMap
          mapId={mapId}
          bounds={bounds}
          mapProps={{
            onClick: !isReadOnly ? clickMap : undefined,
            onMoveEnd,
          }}
          hideExport
        >
          {value ? (
            <Marker
              color={keepAnalytic}
              longitude={value.geometry.coordinates[0]}
              latitude={value.geometry.coordinates[1]}
              anchor="center"
            />
          ) : null}

          <RanaMap.ControlsTopLeft>
            {error ? (
              <Box bg="semantic.error.main" color="brand.glacier" margin={2} padding={2}>
                {error}
              </Box>
            ) : null}
            {!isReadOnly && extra ? (
              <Flex
                h="small_button"
                flexDirection="row"
                alignItems="center"
                color="brand.deep_sea"
                rounded="base"
                px={4}
                w="full"
                maxW="11/12"
                gap={2}
              >
                <Box w="fit" whiteSpace="nowrap" title={extra}>
                  {extra}
                </Box>
                <CancelIcon
                  boxSize={4}
                  color="brand.analytic"
                  onClick={clearValue}
                  cursor="pointer"
                />
              </Flex>
            ) : null}
          </RanaMap.ControlsTopLeft>

          {!isReadOnly && (
            <RanaMap.ControlsBottom>
              <Checkbox
                p={3}
                whiteSpace="nowrap"
                title={intl.formatMessage({
                  id: "map.filter_locations_by_map",
                  defaultMessage:
                    "This will only show the locations you can see on the map in the location input on the left.",
                })}
                isChecked={filterGeoByMapBbox}
                onChange={() => setFilterGeoByMapBbox(!filterGeoByMapBbox)}
              >
                <FormattedMessage
                  id="map.update_results"
                  defaultMessage="Update results while zooming"
                />
              </Checkbox>
            </RanaMap.ControlsBottom>
          )}
        </RanaMap>
      </Box>
    </Box>
  );
}
